import { createContext, useState, useMemo, useEffect } from "react";

import { Table, Space, Button, Tag } from "antd";

import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import DragHandle from "../../ui/DragHandle";

import { FIELD_TYPES_OPTIONS, DEFINITIONS } from "@/constants/definitions";

import PlusSquareIcon from "@/icons/PlusSquareIcon";
import CheckIcon from "@/icons/CheckIcon";
import TrashIcon from "@/icons/TrashIcon";

const RowContext = createContext({});

const Row = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props["data-row-key"],
  });
  const style = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging
      ? {
          position: "relative",
          zIndex: 9999,
        }
      : {}),
  };
  const contextValue = useMemo(
    () => ({
      setActivatorNodeRef,
      listeners,
    }),
    [setActivatorNodeRef, listeners]
  );
  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

// Таблица полей формы представления (вариант отображения)
// Тут можно настроить колонки
const FormFieldsTable = ({
  data = [],
  pagination,
  onAddField,
  onChange,
  onDeleteItem,
  showMulti = false,
  hiddenControls = false,
  showDescription = true,
}) => {
  const [dataSource, setDataSource] = useState(data);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setDataSource(data);
  }, [data]);

  const columns = [
    !hiddenControls && {
      key: "sort",

      width: 40,
      render: (_, field) => (
        <DragHandle context={RowContext} disabled={field.disable} />
      ),
    },
    {
      title: "Название",
      dataIndex: "name",
      key: "name",
      width: 250,
      render: (name, field) => {
        return name || field.caption;
      },
    },

    {
      title: "Тип",
      dataIndex: "type",
      key: "type",

      render: (type, field) => {
        if (DEFINITIONS.includes(type)) {
          return <Tag>{field.object_name || field.uuid}</Tag>;
        } else {
          const t = FIELD_TYPES_OPTIONS.find((item) => item.value === type);
          return t ? <Tag color={t.color}>{t.label}</Tag> : <Tag>{type}</Tag>;
        }
      },
    },

    showMulti
      ? {
          title: "Множественный",
          dataIndex: "is_multiselect",
          key: "is_multiselect",
          render: (is_multiselect) => {
            return is_multiselect ? (
              <CheckIcon style={{ color: "green", fontSize: 16 }} />
            ) : null;
          },
        }
      : null,

    showDescription
      ? {
          title: "Описание",
          dataIndex: "description",
          key: "description",
        }
      : null,

    !hiddenControls && {
      title: "",
      width: 50,
      fixed: "right",
      align: "center",
      key: "actions",
      render: (_, item) =>
        !item.disable && (
          <Space>
            {/* <Button
            type="link"
            icon={<EditTwoTone />}
            onClick={() => onEditItem(item)}
          ></Button> */}
            <Button
              type="link"
              icon={<TrashIcon />}
              onClick={() => onDeleteItem(item)}
            ></Button>
          </Space>
        ),
    },
  ].filter(Boolean);

  const onDragEnd = async ({ active, over }) => {
    if (active.id !== over?.id) {
      setLoading(true);
      const activeField = data.find((i) => i.uuid === active?.id);
      const activeIndex = data.findIndex((i) => i.uuid === active?.id);
      const overIndex = data.findIndex((i) => i.uuid === over?.id);
      const a = arrayMove(dataSource, activeIndex, overIndex);
      setDataSource([...a]);

      await onChange({ ...activeField, idx: overIndex });
      setLoading(false);
    }
  };

  return (
    <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
      <SortableContext
        items={dataSource.map((i) => i.uuid)}
        strategy={verticalListSortingStrategy}
      >
        <Table
          dataSource={dataSource}
          columns={columns}
          rowKey="uuid"
          loading={loading}
          pagination={pagination}
          onChange={onChange}
          showSorterTooltip={{
            target: "sorter-icon",
            title: "Нажмите для сортировки",
          }}
          size="small"
          scroll={{
            x: "100%",
          }}
          components={{
            body: {
              row: Row,
            },
          }}
          footer={
            onAddField
              ? () => (
                  <Button
                    color="default"
                    variant="link"
                    onClick={onAddField}
                    style={{ marginLeft: 40, paddingInline: 0 }}
                  >
                    <PlusSquareIcon style={{ fontSize: 14 }} />
                    Добавить фильтр
                  </Button>
                )
              : undefined
          }
        />
      </SortableContext>
    </DndContext>
  );
};

export default FormFieldsTable;
